// One revision per item (server/src/item-release.ts; Mitch, 2026-11-02: "materials do not carry a BOM. Sub-assemblies
// would always carry a BOM"): a sub-assembly's or a product's spec is released with its BOM under one number, so its
// spec says where the BOM is and how it stands. A material's spec has none.
import { Boxes, Plus } from "lucide-react";
import { currentRevision } from "../../../../shared/boms.ts";
import type { Item, Spec } from "../../../../shared/db.gen.ts";
import { bomLink, createBom, useBomIndex } from "../../lib/boms.ts";
import { can } from "../../lib/mutate.ts";
import { Link } from "../../lib/router.tsx";
import { Button } from "../../ui/controls.tsx";

/** The family a spec's BOM is in: a product's, a sub-assembly's; null for anything else (materials, packaging). */
export const specBomKey = (spec: Pick<Spec, "shopify_product_id">, item: Pick<Item, "id" | "kind">) =>
  spec.shopify_product_id ? `product:${spec.shopify_product_id}` : item.kind === "subassembly" ? `item:${item.id}` : null;

export function ItemBomStrip({ item, spec }: { item: Item; spec: Spec }) {
  const ix = useBomIndex();
  const key = specBomKey(spec, item);
  if (!key) return null;
  const family = ix.families.get(key) ?? []; // newest first
  const inUse = currentRevision(ix, key);
  const draft = family.find((b) => b.status === "draft");
  const open = draft ?? inUse ?? family[0];
  const product = !!spec.shopify_product_id;
  return (
    <div className="mx-2 mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-line bg-surface-2 px-2.5 py-1.5 text-[12px]">
      <Boxes className="size-3.5 shrink-0 text-muted" />
      {open ? (
        <>
          <span>
            BOM{" "}
            {[
              inUse?.status === "active" ? `Rev ${inUse.revision} in use` : null,
              draft ? `Rev ${draft.revision} draft` : null,
              !draft && inUse?.status !== "active" ? `Rev ${open.revision} ${open.status}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}{" "}
            <span className="text-muted">· {ix.linesByBom.get(open.id)?.length ?? 0} lines</span>
          </span>
          <span className="text-muted">Released with this spec as one revision.</span>
          <Link to={bomLink(open.id)} className="ml-auto text-accent-text hover:underline">
            Open BOM
          </Link>
        </>
      ) : (
        <>
          <span className="text-muted">
            {product ? "No BOM yet: the product's BOM and this spec release together." : "No BOM yet: a sub-assembly is built from one."}
          </span>
          {can("bom.create") && (
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto"
              icon={<Plus className="size-3.5" />}
              onClick={() => createBom(ix, product ? { shopify_product_id: spec.shopify_product_id as string } : { item_id: item.id, project_ids: [] })}
            >
              {product ? "Start the product's BOM" : "Start its BOM"}
            </Button>
          )}
        </>
      )}
    </div>
  );
}
